import { faDownload, faFileDownload, faTrash } from "@fortawesome/pro-duotone-svg-icons";
import { Button, Checkbox, Flex, Grid, Group, Select, Text, TextInput, Tooltip } from "@mantine/core";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Icon } from "../../../custom-components/icons";
import Modal from "../../../custom-components/model";
import PopConfirm from "../../../custom-components/pop-confirm";
import { TableView } from "../../../custom-components/table";
import { QueryKeys, ReportData, FormulaResult, FormulaElement } from "../../../models/models";
import t from "../../../stores/language";
import { useQuery, useStore } from "../../../stores/store";
import theme from "../../../stores/theme";
import { useStyles } from "../configure-input";
import ResultView, { sortResults } from "../result-view";
import { getSortedData } from "../configure-formula";
import ReportSave, { defaultReport, ReportSaveHandle } from "./report-save";
import { getDetailedResult } from "../module-result-view";

const ReportsView = observer(function ReportsView() {
  const { classes } = useStyles();
  const { store } = useStore();
  const { moduleId: module } = useParams();
  const inputElements = store.GetInputByModule(module ?? "");
  const [selectedReports, setSelectedReports] = useState<{ [key: string]: string | null | number }[]>([]);
  const [error, setError] = useState<{ isError: boolean; message: string }>({ isError: false, message: "" });
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedReport, setSelectedReport] = useState<ReportData | null>(null);
  const [selectedLookupYear, setSelectedLookupYear] = useState<string | null>(null);
  const [reportInputElements, setReportInputElements] = useState<Record<string, string> | null>(null);
  const [reportInputElementsForNewYear, setReportInputElementsForNewYear] = useState<Record<string, string> | null>(null);

  const reportSaveRef = useRef<ReportSaveHandle>(null);
  const [calculatedFormulaResult, setCalculatedFormulaResult] = useState<{ [key: string]: string | number | null }[]>([]);
  const moduleById = store.GetModuleById(module ?? "0");
  const formulaElementsOrder = (moduleById?.moduleConfig?.formulaElementsOrder ?? []).map((ie) => ie.toString());
  const formulaElements = store.GetFormulasByModule(module ?? "");
  const orderedFormulaElements = formulaElementsOrder.length > 0 ? (getSortedData(formulaElementsOrder, formulaElements) as FormulaElement[]) : formulaElements;
  const orderedData = formulaElementsOrder.length > 0 ? sortResults(store.results, orderedFormulaElements) : store.results;
  const formulasToShowInDetailedResult = orderedFormulaElements.filter((formula) => formula.formulaConfig?.hideInDetailResults != true);
  const formulasToCalculate = orderedFormulaElements.filter((formula) => formula.formulaConfig?.showInCalculate == true);

  const nonCommentsGroups = inputElements.filter((input) => input.inputConfig?.group !== "Comments");
  const commentsGroups = inputElements.filter((input) => input.inputConfig?.group === "Comments");

  const { data: lookupDataPageNo } = useQuery({
    queryKey: [QueryKeys.LookupDataPageNo],
    queryFn: async () => {
      return await store.GetAllLookupDataPageNo();
    },
  });

  const { data: reports, isLoading: loading } = useQuery({
    queryKey: [QueryKeys.Reports],
    queryFn: async () => {
      return await store.GetAllReports();
    },
  });

  const deleteReport = (id: number) => {
    store.DeleteReport(id);
  };

  const downloadReportData = async (moduleId?: number, reportId?: number) => {
    const selectedModule = moduleId == null ? [...new Set(selectedReports.map((r) => r.moduleId as number))] : [moduleId];
    if (selectedModule.length > 1) {
      setError({ isError: true, message: t("Please Select Reports of Same Module") });
      return;
    }
    const result = await store.DownloadReportsData(
      selectedModule[0],
      reportId == null ? selectedReports.map((r) => parseInt(r.id?.toString() ?? "0")) : [reportId]
    );
    if (result) {
      const moduleData = new Blob([result], { type: "text/csv;charset=utf-8;" });
      const fileURL = window.URL.createObjectURL(moduleData);
      const tempLink = document.createElement("a");
      tempLink.href = fileURL;
      if (reportId != null) {
        tempLink.setAttribute("download", `${reports?.find((r) => r.id === reportId)?.name ?? "report"}.csv`);
      } else {
        tempLink.setAttribute("download", `${store.GetModuleById(selectedModule[0].toString())?.name ?? "report"}.csv`);
      }
      tempLink.click();
    } else {
      return;
    }
  };
  const downloadInterModuleReportData = async () => {
    const result = await store.DownloadInterModuleReportsData(selectedReports.map((r) => parseInt(r.id?.toString() ?? "0")));
    if (result) {
      store.downloadReportArchive(result);
    }
  };

  const refresh = async () => {
    await store.EvaluateFormulas(reportInputElementsForNewYear ?? {}, module ?? "0").then((data: FormulaResult | null) => {
      if (data == null) return;
      store.SetResults(data?.result ?? []);
      store.SetErrors(data?.errors ?? []);
    });
  };

  const calculateSelectedFormulaResult = () => {
    if (store.resultErrors.length == 0 && store.results.length > 0) {
      const filteredResult = store.results.filter((result) => {
        return formulasToCalculate.some((formula) => formula.name === result["Renewal Steps/Components"]);
      });
      let calculatedResult = [];
      if (filteredResult.length > 0) {
        calculatedResult = sortResults(filteredResult, orderedFormulaElements).map((r) => {
          return {
            "Renewal Steps/Components": r["Renewal Steps/Components"]?.toString() ?? "",
            Cost: r["Cost"],
          };
        });
      } else {
        calculatedResult = formulasToCalculate.map((formula) => {
          return {
            "Renewal Steps/Components": formula.name,
            Cost: 0,
          };
        });
      }
      setCalculatedFormulaResult(calculatedResult);
    } else {
      setCalculatedFormulaResult([]);
    }
  };

  const handleRecalculate = async () => {
    await refresh();
    calculateSelectedFormulaResult();
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedReports(reports?.filter((report) => module == "all" || report.moduleId == parseInt(module ?? "0")) || []);
    } else {
      setSelectedReports([]);
    }
  };

  const getReportInputElementsBasedOnNewYear = async (report: ReportData) => {
    const reportToUse = report || selectedReport;
    if (reportToUse) {
      const inputElements = await store.getReportInputElementsBasedOnReportId(reportToUse.id.toString());
      setReportInputElements(inputElements);
    }
  };

  const filteredReports = reports?.filter((report) => module === "all" || report.moduleId === parseInt(module ?? "0")) ?? [];
  const allSelected = selectedReports.length > 0 && selectedReports.length === filteredReports.length;

  const getFormulaResultValue = (formulaResult: Record<string, string>, key: string): string | undefined => {
    if (!formulaResult) return undefined;

    return formulaResult[key];
  };

  const downloadAssumptions = async (id: number) => {
    const report = store.GetReportById(id);
    if (report == null) {
      return;
    }
    const result = await store.DownloadModuleAssumption(report.moduleId);
    if (result) {
      const moduleData = new Blob([result], { type: "text/html" });
      const fileURL = window.URL.createObjectURL(moduleData);
      const tempLink = document.createElement("a");
      tempLink.href = fileURL;
      tempLink.setAttribute("download", `${report.name}.html`);
      tempLink.click();
    } else {
      return;
    }
  };

  const GetActions = (row: { [key: string]: string | null | number }) => {
    return (
      <Flex justify={"center"} gap={5}>
        <Tooltip label={t("Download Report Data")} withArrow withinPortal>
          <Button
            size="xs"
            variant="subtle"
            compact
            className={classes.iconButton}
            onClick={() => downloadReportData(row.moduleId as number, row.id as number)}
          >
            <Icon icon={faDownload} size="xl" />
          </Button>
        </Tooltip>
        <Tooltip label={t("Download Standard Assumptions")} withArrow withinPortal>
          <Button size="xs" variant="subtle" compact className={classes.iconButton} onClick={() => downloadAssumptions(row.id as number)}>
            <Icon icon={faFileDownload} size="xl" />
          </Button>
        </Tooltip>
        <PopConfirm confirmText={t("Are You Sure?")} width={180} onConfirm={() => deleteReport(row.id as number)}>
          <Tooltip label={t("Delete")} withArrow withinPortal>
            <Button size="xs" variant="subtle" compact className={classes.iconButton}>
              <Icon icon={faTrash} size="xl" color={"red"} />
            </Button>
          </Tooltip>
        </PopConfirm>
      </Flex>
    );
  };

  const rowSelection = (row: { [key: string]: string | null | number }) => {
    return (
      <Flex align={"center"} justify={"center"}>
        <Checkbox
          label={""}
          checked={selectedReports.find((report) => report.id == row.id) != null}
          onChange={() => {
            if (error.isError) {
              setError({ isError: false, message: "" });
            }
            const checked = selectedReports.find((report) => report.id == row.id);
            if (checked == null) {
              setSelectedReports([...selectedReports, row]);
            } else {
              setSelectedReports(selectedReports.filter((report) => report.id != row.id));
            }
          }}
          size={"xs"}
        />
      </Flex>
    );
  };

  useEffect(() => {
    if (selectedLookupYear !== null && reportInputElements !== null) {
      const yearInput = inputElements.find((ele) => ele.type === "Year Input");
      const yearInputId = yearInput?.id?.toString();

      if (yearInputId && reportInputElements[yearInputId] !== undefined) {
        const reportInputClone = { ...reportInputElements };
        reportInputClone[yearInputId] = selectedLookupYear;
        setReportInputElementsForNewYear(reportInputClone);
      }
    }
  }, [selectedLookupYear, reportInputElements, inputElements]);

  return loading ? (
    <></>
  ) : (
    <>
      <div className={classes.reportsContainer}>
        <div className={classes.title}>
          <span>{t("Reports")} </span>
          <Flex gap={5}>
            <Button
              size="xs"
              variant="light"
              compact
              style={{ display: "flex", flexDirection: "row", justifyContent: "center" }}
              onClick={() => setModalOpen(true)}
            >
              {t("Recalculate by New Year")}
            </Button>
            {selectedReports.length > 0 ? (
              <Tooltip label={t("Download Selected Reports")} withArrow withinPortal>
                <Button
                  size="xs"
                  variant="subtle"
                  compact
                  className={classes.iconButton}
                  onClick={module === "all" ? () => downloadInterModuleReportData() : () => downloadReportData()}
                >
                  <Icon icon={faDownload} size="xl" />
                </Button>
              </Tooltip>
            ) : null}
          </Flex>
        </div>
        {error.message !== "" ? (
          <Text align="right" size={theme.fontSizes?.sm} color={error.isError ? "red" : "green"}>
            {error.message}
          </Text>
        ) : null}
        <TableView
          data={
            reports
              ?.filter((report) => module == "all" || report.moduleId == parseInt(module ?? "0"))
              ?.map((r) => {
                return {
                  name: r.name,
                  moduleName: store.GetModuleById(r.moduleId.toString())?.name ?? "",
                  id: r.id,
                  moduleId: r.moduleId,
                  year: r.year,
                };
              })
              .sort((d) => d.moduleId) ?? []
          }
          columns={[
            { key: "moduleName", label: "Module Name", width: "47%" },
            { key: "name", label: "Name", width: "47%" },
            { key: "year", label: "Year", width: "6%" },
          ]}
          height={"calc(100vh - 206px)"}
          actions={GetActions}
          selectRows={rowSelection}
          headerAlign={{ Actions: "center" }}
          onSelectAll={handleSelectAll}
          allSelected={allSelected}
        />
      </div>
      <Modal
        className={classes.configureModal}
        opened={modalOpen}
        header={t("Recalculate for New Year")}
        footer={
          <Group
            className={classes.modalFooter}
            style={{
              paddingTop: "8px",
              marginTop: "auto",
              position: "sticky",
              bottom: 0,
              backgroundColor: "white",
              zIndex: 10,
            }}
          >
            <Button
              variant={"default"}
              onClick={() => {
                setSelectedReport(null);
                setSelectedLookupYear(null);
                setCalculatedFormulaResult([]);
                setModalOpen(false);
              }}
            >
              {t("Cancel")}
            </Button>
            <Button disabled={selectedReport === null || selectedLookupYear === null} onClick={handleRecalculate}>
              {t("Recalculate")}
            </Button>
            {calculatedFormulaResult.length > 0 && (
              <Button
                onClick={() =>
                  reportSaveRef.current?.show({
                    ...defaultReport,
                    name: (selectedReport?.name ?? "") + " (" + (selectedLookupYear ?? "") + ")",
                    moduleId: parseInt(module?.toString() ?? "0"),
                    selectedInputs: reportInputElementsForNewYear,
                    formulasResult: {
                      detailedResult: getDetailedResult(orderedData, formulasToShowInDetailedResult),
                      calculatedResult: calculatedFormulaResult,
                    },
                  })
                }
              >
                {t("Save")}
              </Button>
            )}
          </Group>
        }
        withCloseButton={true}
        onClose={() => {
          setSelectedReport(null);
          setSelectedLookupYear(null);
          setCalculatedFormulaResult([]);
          setModalOpen(false);
        }}
        size={"90%"}
      >
        <Grid pb={15} pl={15} pr={15} columns={12} m={0}>
          <Grid.Col span={4}>
            <Select
              label={t("Report")}
              data={reports?.filter((r) => r.moduleId.toString() === module).map((report) => report.name) ?? []}
              searchable
              clearable
              value={selectedReport?.name}
              onChange={(e) => {
                const report = reports?.find((report) => report.name === e);
                if (report) {
                  setSelectedReport(report);
                  getReportInputElementsBasedOnNewYear(report);
                } else {
                  setSelectedLookupYear(null);
                }
                setCalculatedFormulaResult([]);
              }}
            />
          </Grid.Col>
          <Grid.Col span={4}>
            <TextInput label={t("Current Year")} value={reports?.map((report) => report.year)[0]} disabled />
          </Grid.Col>
          <Grid.Col span={4}>
            <Select
              onChange={(value) => {
                setSelectedLookupYear(value);
                setCalculatedFormulaResult([]);
              }}
              clearable
              value={selectedLookupYear}
              data={lookupDataPageNo ? Object.keys(lookupDataPageNo).sort((a, b) => parseInt(a) - parseInt(b)) : []}
              label={t("Recalculate for New Year")}
              searchable
              nothingFound={t("No year found")}
              maxDropdownHeight={150}
              placeholder={t("Select a year")}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <div style={{ fontWeight: "bold", fontSize: theme.fontSizes?.sm, marginTop: "4px" }}>Inputs</div>
            <Grid
              style={{
                marginBottom: theme.other?.arc.margins[1],
                marginTop: theme.other?.arc.margins[0],
                marginLeft: theme.other?.arc.margins[0],
                marginRight: theme.other?.arc.margins[0],
                padding: theme.other?.arc.paddings[1],
                border: `1px solid ${theme.colors?.gray?.[3] ?? "#e2e2e2"}`,
                maxHeight: 85,
                overflowY: "auto",
                flexShrink: 0,
              }}
            >
              {nonCommentsGroups.map((input) => (
                <Grid.Col span={4} key={input.id} style={{ paddingTop: 0, paddingBottom: 0 }}>
                  <span style={{ fontWeight: "bold", fontSize: theme.fontSizes?.sm }}>{input.name} - </span>
                  <span style={{ marginLeft: 2, fontSize: theme.fontSizes?.sm }}>{getFormulaResultValue(reportInputElements ?? {}, input.id.toString())}</span>
                </Grid.Col>
              ))}
            </Grid>
            {commentsGroups.length > 0 && (
              <>
                <div style={{ fontWeight: "bold", fontSize: theme.fontSizes?.sm, marginTop: "4px" }}>Comments</div>
                <Grid
                  style={{
                    marginBottom: theme.other?.arc.margins[1],
                    marginTop: theme.other?.arc.margins[0],
                    marginLeft: theme.other?.arc.margins[0],
                    marginRight: theme.other?.arc.margins[0],
                    padding: theme.other?.arc.paddings[1],
                    border: `1px solid ${theme.colors?.gray?.[3] ?? "#e2e2e2"}`,
                    maxHeight: 85,
                    overflowY: "auto",
                    flexShrink: 0,
                  }}
                >
                  {commentsGroups.map((input) => (
                    <Grid.Col span={4} key={input.id} style={{ paddingTop: 0, paddingBottom: 0 }}>
                      <span style={{ fontWeight: "bold", fontSize: theme.fontSizes?.sm }}>{input.name} - </span>
                      {/* <span style={{ marginLeft: 2, fontSize: theme.fontSizes?.sm }}>{selectorValue && selectorValue[input.id]}</span> */}
                    </Grid.Col>
                  ))}
                </Grid>
              </>
            )}
          </Grid.Col>
          <Grid.Col span={12}>
            <ResultView id={selectedReport?.moduleId.toString()} />
          </Grid.Col>
        </Grid>
      </Modal>
      <ReportSave
        ref={reportSaveRef}
        closeAndReset={{
          closeMainModal: () => setModalOpen(false),
          resetReport: () => setSelectedReport(null),
          resetSelectedLookupYear: () => setSelectedLookupYear(null),
          resetCalculatedFormulaResult: () => setCalculatedFormulaResult([]),
        }}
      />
    </>
  );
});

export default ReportsView;
